<template>
  <div class="min-h-screen" data-theme="yellow">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="forum" />
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-8">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('forum.title') }}</h1>
        <button v-if="signedIn" @click="showNewTopic = true" class="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800 transition">
          {{ t('forum.new_topic') }}
        </button>
      </div>

      <!-- New topic form -->
      <div v-if="showNewTopic" class="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <form @submit.prevent="createTopic" class="flex gap-3">
          <input v-model="newTopicTitle" type="text" :placeholder="t('forum.topic_title_placeholder')" required
                 class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button type="submit" class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition">{{ t('forum.create') }}</button>
          <button type="button" @click="showNewTopic = false" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">{{ t('forum.cancel') }}</button>
        </form>
      </div>

      <!-- Flash -->
      <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">
        {{ flash.message }}
      </div>

      <!-- Topics list -->
      <div v-if="topics.length === 0" class="text-center py-12 text-gray-500">{{ t('forum.no_topics') }}</div>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
        <div v-for="(topic, index) in topics" :key="topic.id" :class="['p-4 hover:bg-blue-50 transition flex items-center justify-between', index % 2 === 1 ? 'bg-blue-50' : 'bg-white']">
          <a :href="`/forum/${topic.id}`" class="flex-1">
            <h3 class="text-sm font-semibold text-gray-900 hover:text-blue-700">{{ topic.title }}</h3>
            <p class="text-xs text-gray-500 mt-1">
              {{ topic.user.name }} · {{ formatDate(topic.created_at) }} · {{ topic.messages_count }} {{ t('forum.messages') }}
            </p>
            <p v-if="topic.last_message" class="text-xs text-gray-400 mt-0.5">
              {{ t('forum.last_message') }}: {{ topic.last_message.user_name }} · {{ formatDateTime(topic.last_message.created_at) }}
            </p>
          </a>
          <button v-if="topic.user.id === userId || userAdmin" @click="deleteTopic(topic)"
                  class="text-xs text-red-400 hover:text-red-600 ml-4">{{ t('forum.delete') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, userAdmin: Boolean, topics: Array, csrfToken: String })
const { t, locale } = useI18n()

const showMenu = ref(false)
const logoSize = ref(120)
const showNewTopic = ref(false)
const newTopicTitle = ref('')
const flash = ref(null)

function formatDate(d) { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
function formatDateTime(d) { const dt = new Date(d); return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) }
function showFlash(msg, type = 'success') { flash.value = { message: msg, type }; setTimeout(() => { flash.value = null }, 4000) }

async function request(url, method, body = null) {
  const o = { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' } }
  if (body) o.body = JSON.stringify(body)
  return fetch(url, o)
}

function createTopic() {
  // Use regular form submit for redirect
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = '/forum'
  const csrf = document.createElement('input'); csrf.type = 'hidden'; csrf.name = 'authenticity_token'; csrf.value = props.csrfToken
  const title = document.createElement('input'); title.type = 'hidden'; title.name = 'forum_topic[title]'; title.value = newTopicTitle.value
  form.appendChild(csrf); form.appendChild(title)
  document.body.appendChild(form)
  form.submit()
}

async function deleteTopic(topic) {
  if (!confirm(t('forum.confirm_delete'))) return
  try {
    const res = await request(`/forum/${topic.id}`, 'DELETE')
    if (res.ok) window.location.reload()
  } catch { showFlash(t('forum.error'), 'error') }
}
</script>
